"use client";

import type { ProductForCard } from "@/components/storefront/ProductCard";
import { ProductCard } from "@/components/storefront/structured-widgets/ecommerce/ProductCard";
import type { ProductData } from "@/components/storefront/structured-widgets/ecommerce/types";

/**
 * The listing DTO carries a variable product's cheapest and dearest prices,
 * not its variants. The glass card reads a range from `product_variants`, so
 * the bounds stand in as price-only rows. Their stock is unknown (null), and
 * the card's button opens the option picker, which loads the real variants.
 */
export function listingToCardData(p: ProductForCard): ProductData {
  const bounds =
    p.product_type === "variable" && p.min_variant_price != null
      ? [p.min_variant_price, p.max_variant_price ?? p.min_variant_price]
      : p.product_type === "variable"
        ? [p.price]
        : [];
  return {
    id: p.id,
    slug: p.slug,
    name_ar: p.name_ar,
    name_en: p.name_en,
    price: p.price,
    sale_price: p.sale_price ?? null,
    original_price: p.original_price ?? null,
    description_ar: null,
    description_en: null,
    product_type: p.product_type ?? "simple",
    min_order_qty: p.min_order_qty ?? null,
    qty_step: p.qty_step ?? null,
    track_stock: p.track_stock ?? false,
    stock: p.stock ?? null,
    option_names: [],
    product_images: p.coverImageUrl
      ? [{ url: p.coverImageUrl, display_order: 0, blur_data_url: p.coverImageBlur ?? null }]
      : [],
    product_variants: [...new Set(bounds)].map((price, i) => ({
      id: `${p.id}-bound-${i}`,
      options: {},
      price,
      stock: null,
      image_url: null,
      is_active: true,
      min_order_qty: null,
    })),
    specs: p.specs ?? [],
    base_unit: p.base_unit ?? null,
    units: p.units ?? [],
  };
}

export default function GlassListingCard({
  product,
  locale,
}: {
  product: ProductForCard;
  locale: string;
}) {
  return (
    <ProductCard
      product={listingToCardData(product)}
      hexMap={{}}
      locale={locale}
      variant="glass-spec"
      shape="square"
      showAddToCart
    />
  );
}
